<script lang="ts">
  import { storedColor } from '$lib/utils/colors';
  import CommandPalette from './ui/CommandPalette.svelte';
  import {
    Building2,
    Users,
    Kanban,
    Activity,
    Inbox,
    Wrench,
    PhoneCall,
    ShieldCheck,
    ChevronDown,
    Menu,
    LogOut,
    Settings,
    LayoutGrid,
    Search,
    Plus,
    X,
    Mail,
    FileDown,
    MapPin,
    Landmark
  } from 'lucide-svelte';
  import { createQuery } from '@tanstack/svelte-query';
  import type { Snippet } from 'svelte';
  import { session } from '$lib/state/session.svelte';
  import { route, type Route } from '$lib/state/route.svelte';
  import { leadsApi } from '$lib/api/leads';
  import { companiesApi, peopleApi, dealsApi } from '$lib/api/crm';
  import { ordersEnabled } from '$lib/utils/orders';
  import { canUseHappyQueue } from '$lib/utils/happyCode';
  import { roleProfile, rolePresentation, workflowAccessKey } from '$lib/utils/roleProfile';
  import { happyApi } from '$lib/api/happyCode';
  import { assignmentReadsApi } from '$lib/api/assignmentReads';
  import type { Company, Person, Deal } from '$lib/types';

  const {
    activeTab,
    onNavigate,
    onLogout,
    onLog,
    children
  }: {
    activeTab: string;
    onNavigate: (tab: string) => void;
    onLogout: () => void;
    onLog?: () => void;
    children: Snippet;
  } = $props();

  // Nav order per the redesign brief: Dashboard, Leads, Issues, Deals,
  // Companies, People, Activities (labeled "Touchpoints" in the UI — the
  // route id, API path and model stay `activities`), Admin (Admin rendered
  // separately below, gated). An issues_only (field-technician) account
  // only ever sees Issues — everything else 403s server-side anyway (see
  // middleware.RequireNotIssuesOnly), so there is nothing else to link to.
  type NavItem = { id: string; label: string; icon: typeof Building2 };
  const fullNavItems: NavItem[] = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutGrid },
    { id: 'leads', label: 'Leads', icon: Inbox },
    { id: 'issues', label: 'Issues', icon: Wrench },
    { id: 'deals', label: 'Deals', icon: Kanban },
    { id: 'dispatch', label: 'Billing & dispatch', icon: Kanban },
    { id: 'companies', label: 'Companies', icon: Building2 },
    { id: 'people', label: 'Contacts', icon: Users },
    { id: 'activities', label: 'Agenda', icon: Activity },
    { id: 'reports', label: 'Reports', icon: FileDown }
  ];
  const issuesOnlyNavItems: NavItem[] = [{ id: 'issues', label: 'Issues', icon: Wrench }];
  const isIssuesOnly = $derived(session.me?.user.issues_only ?? false);
  const isDispatchOnly = $derived(session.me?.user.dispatch_only ?? false);
  // /me may omit the capability on older servers. In that case let the
  // queue endpoint itself establish support access; ordinary CRM membership
  // is never a support grant. Coordinators additionally qualify through the
  // shared helper's explicit target-pipeline check.
  const happyAccess = createQuery(() => ({
    queryKey: ['happy-code-queue', roleProfile(session.me), session.me?.user.id, workflowAccessKey(session.me)], queryFn: happyApi.queue,
    enabled: !isIssuesOnly && !isDispatchOnly && !session.isAdmin && session.me?.happy_code_queue_access === undefined,
    retry: false
  }));
  // "Sites" (an operational, per-property/per-project alternative to the
  // Deals pipeline) only makes sense for a single selected vertical whose
  // slug is hotels or lighting — same "single vertical selected" gating
  // idea as DealsPage's board view, keyed off the vertical's slug instead of
  // its configured deal_stages. Inserted right after Deals in the nav.
  const selectedVerticalSlug = $derived(
    typeof session.selectedVertical === 'number'
      ? session.me?.verticals.find((v) => v.id === session.selectedVertical)?.slug
      : undefined
  );
  const showSites = $derived(selectedVerticalSlug === 'hotels' || selectedVerticalSlug === 'lighting' || ordersEnabled(session.me?.verticals.find(v => v.id === session.selectedVertical)));
  // Hotels swaps the generic "Companies" nav destination for "Owners" (see
  // the Hotels nav restructure spec) — Companies stays a real route (deep
  // links, other verticals, and a low-key cleanup entry under Admin), it
  // just isn't a main-nav item here. Every other vertical's nav is untouched.
  const isHotelsVertical = $derived(selectedVerticalSlug === 'hotels');
  const navItems = $derived.by(() => {
    if (isDispatchOnly) return [{ id: 'dispatch', label: 'Dispatch', icon: Kanban }];
    if (isIssuesOnly) return issuesOnlyNavItems;
    let items = isHotelsVertical
      ? fullNavItems.map((item) => (item.id === 'companies' ? { id: 'owners', label: 'Owners', icon: Landmark } : item))
      : fullNavItems;
    if (canUseHappyQueue(session.me?.user, session.me?.happy_code_queue_access ?? happyAccess.isSuccess, session.me?.verticals)) items = [...items, { id: 'happy-code-queue', label: 'Call queue', icon: PhoneCall }];
    if (showSites) {
      items = [...items.slice(0, 4), { id: 'sites', label: 'Sites', icon: MapPin }, ...items.slice(4)];
    }
    return [{ id: 'my-day', label: 'My day', icon: LayoutGrid }, ...items];
  });
  // Nav hygiene only — GET /api/me/email-review etc. 403 server-side
  // regardless of what's shown here (see middleware.RequireEmailAccess).
  const canSeeEmails = $derived(!isIssuesOnly && !isDispatchOnly && (session.me?.user.email_access || session.isAdmin));

  const pageMeta: Record<string, { title: string; subtitle: string }> = {
    dispatch: { title: 'Billing & dispatch', subtitle: 'Invoice, printed Deal ID & shipping manifest' },
    'my-day': { title: 'My day', subtitle: 'Your next customer conversation' },
    'happy-code-queue': { title: 'Call queue', subtitle: 'Everyone to call today, most urgent first' },
    dashboard: { title: 'Dashboard', subtitle: 'Cross-vertical overview' },
    companies: { title: 'Companies', subtitle: 'Businesses, sites & customer accounts' },
    people: { title: 'Contacts', subtitle: 'One customer, one workspace, one next step' },
    deals: { title: 'Deals', subtitle: 'Pipeline & stages' },
    sites: { title: 'Sites', subtitle: 'Per-property/project operational status' },
    owners: { title: 'Owners', subtitle: 'Owner groups & their sites' },
    activities: { title: 'Agenda', subtitle: 'Planned work, conversations and next steps' },
    leads: { title: 'Leads', subtitle: 'Triage the inbox' },
    issues: { title: 'Issues', subtitle: 'Customer care, feedback & service requests' },
    admin: { title: 'Admin', subtitle: 'Settings & configuration' },
    settings: { title: 'Settings', subtitle: 'Your email connection' },
    emails: { title: 'Emails', subtitle: 'Connect your inbox & review new senders' },
    reports: { title: 'Reports', subtitle: 'Download entity exports' }
  };

  let mobileNavOpen = $state(false);
  let vertMenuOpen = $state(false);

  const verticalId = $derived(session.selectedVertical === 'all' ? undefined : session.selectedVertical);

  const assignmentCounts = createQuery(() => ({
    queryKey: ['assignment-counts', session.me?.user.id],
    queryFn: assignmentReadsApi.counts,
    enabled: !isDispatchOnly,
    refetchInterval: 60000,
    retry: false
  }));
  const newLeadsBadge = $derived(assignmentCounts.data?.leads ?? 0);
  const newIssuesBadge = $derived(assignmentCounts.data?.issues ?? 0);

  type VertOption = { id: number | 'all'; name: string; dot: string };

  const verticalOptions = $derived(
    session.me
      ? ([
          ...(session.isAdmin ? [{ id: 'all', name: 'All verticals', dot: '#97D700' }] : []),
          ...session.me.verticals.map((v) => ({ id: v.id, name: v.name, dot: storedColor(v.settings?.color) }))
        ] as VertOption[])
      : []
  );

  const currentVertical = $derived(
    verticalOptions.find((v) => v.id === session.selectedVertical) ?? verticalOptions[0]
  );

  // Writes through the URL (the single source of truth for scope — see
  // session.svelte.ts) rather than session state directly, so every page
  // reacts identically to the dropdown, a hand-edited hash, or reload.
  function selectVertical(id: number | 'all') {
    route.push({ ...route.current, vertical: String(id) });
    vertMenuOpen = false;
  }

  function navigate(tab: string) {
    onNavigate(tab);
    mobileNavOpen = false;
  }

  // ===================== topbar search =====================
  // Fans out to the existing companies/people/deals ?q= list endpoints
  // (respecting the selected vertical), debounced ~300ms. Picking a result
  // navigates to that entity's tab and opens its edit modal pre-filled with
  // the exact row this search already fetched (see session.svelte.ts).
  let searchTerm = $state('');
  let debouncedTerm = $state('');
  let searchFocused = $state(false);
  let mobileSearchOpen = $state(false);
  let paletteOpen = $state(false);
  function onGlobalKeydown(e: KeyboardEvent) {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k' && !isDispatchOnly) {
      e.preventDefault();
      paletteOpen = !paletteOpen;
    }
  }

  $effect(() => {
    const term = searchTerm;
    const handle = setTimeout(() => {
      debouncedTerm = term;
    }, 300);
    return () => clearTimeout(handle);
  });

  const searchQuery = createQuery(() => ({
    queryKey: ['topbar-search', { term: debouncedTerm, verticalId }],
    queryFn: async () => {
      const q = debouncedTerm.trim();
      const [companies, people, deals] = await Promise.all([
        companiesApi.list({ q, per_page: 4, vertical_id: verticalId }),
        peopleApi.list({ q, per_page: 4, vertical_id: verticalId }),
        dealsApi.list({ q, per_page: 4, vertical_id: verticalId })
      ]);
      return { companies: companies.results, people: people.results, deals: deals.results };
    },
    // The topbar search fans out to companies/people/deals, all 403 for an
    // issues_only account (see middleware.RequireNotIssuesOnly) — the search
    // box itself is hidden for that persona (below), so this never runs.
    enabled: debouncedTerm.trim().length > 0 && !isIssuesOnly && !isDispatchOnly
  }));

  const showResults = $derived(searchFocused && debouncedTerm.trim().length > 0);
  const resultCount = $derived(
    (searchQuery.data?.companies.length ?? 0) +
      (searchQuery.data?.people.length ?? 0) +
      (searchQuery.data?.deals.length ?? 0)
  );

  function closeSearch() {
    searchTerm = '';
    debouncedTerm = '';
    searchFocused = false;
    mobileSearchOpen = false;
  }
  function onSearchBlur() {
    // Delayed so a click on a result (onmousedown fires first) still lands.
    setTimeout(() => (searchFocused = false), 150);
  }
  function onSearchKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') closeSearch();
  }

  function pickCompany(c: Company) {
    route.push({ tab: 'companies', id: c.id });
    closeSearch();
  }
  function pickPerson(p: Person) {
    route.push({ tab: 'people', id: p.id });
    closeSearch();
  }
  function pickDeal(d: Deal) {
    route.push({ tab: 'deals', id: d.id });
    closeSearch();
  }

  function personName(p: Person): string {
    return `${p.first_name} ${p.last_name}`.trim() || p.email || '(no name)';
  }

  // ===================== "+ New" dropdown =====================
  let newMenuOpen = $state(false);
  const newMenuItems = [
    { tab: 'companies', label: 'New company' },
    { tab: 'people', label: 'New contact' },
    { tab: 'deals', label: 'New deal' },
    { tab: 'leads', label: 'New lead' },
    { tab: 'activities', label: 'Log or plan' },
    { tab: 'issues', label: 'New issue' }
  ];
  function chooseNew(tab: string) {
    route.push({ tab } as Route, () => session.requestCreate(tab));
    mobileNavOpen = false;
    newMenuOpen = false;
  }

  const initials = $derived(
    (session.me?.user.name || session.me?.user.email || '?')
      .trim()
      .split(/\s+/)
      .map((s) => s[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase()
  );

  const userSubtitle = $derived(
    session.isAdmin ? 'Admin · all verticals' : (session.me?.verticals.map((v) => v.name).join(', ') ?? 'Member')
  );
</script>

<svelte:window onkeydown={onGlobalKeydown} />
{#if paletteOpen}<CommandPalette onClose={() => { paletteOpen = false; }} />{/if}

<div class="flex h-screen overflow-hidden bg-toyama-light-carbon">
  {#if mobileNavOpen}
    <button
      type="button"
      class="fixed inset-0 z-30 bg-[#0A1626]/50 md:hidden"
      aria-label="Close navigation"
      onclick={() => (mobileNavOpen = false)}
    ></button>
  {/if}

  <!-- ===================== SIDEBAR ===================== -->
  <aside
    class="fixed inset-y-0 left-0 z-40 flex w-[248px] shrink-0 flex-col border-r border-[#0A1626] bg-navy transition-transform duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] md:static md:translate-x-0 {mobileNavOpen
      ? 'translate-x-0'
      : '-translate-x-full'}"
  >
    <div class="px-5 pb-4 pt-[22px]">
      <img src="/logo.svg" alt="Toyama" class="block h-[22px]" />
      <div class="mt-2 text-[10px] font-semibold uppercase tracking-[.16em] text-white/40">CRM &middot; Internal</div>
    </div>

    <!-- vertical switcher -->
    <div class="relative px-3.5 pb-3.5 pt-1">
      {#if verticalOptions.length > 1}
        <button
          type="button"
          class="flex min-h-[44px] w-full items-center gap-2.5 rounded-[10px] border border-white/10 bg-white/5 px-3 py-[9px] text-left hover:bg-white/[.09]"
          onclick={() => (vertMenuOpen = !vertMenuOpen)}
          aria-expanded={vertMenuOpen}
        >
          <span class="h-[9px] w-[9px] shrink-0 rounded-full" style="background:{currentVertical?.dot}"></span>
          <span class="min-w-0 flex-1">
            <span class="block text-[9px] font-semibold uppercase tracking-[.1em] text-white/40">Vertical</span>
            <span class="block truncate font-heading text-sm font-bold tracking-tight text-white">
              {currentVertical?.name ?? '—'}
            </span>
          </span>
          <ChevronDown class="h-4 w-4 shrink-0 text-white/50" />
        </button>
        {#if vertMenuOpen}
          <button
            type="button"
            class="fixed inset-0 z-30 cursor-default"
            aria-label="Close vertical menu"
            onclick={() => (vertMenuOpen = false)}
          ></button>
          <div
            class="absolute left-3.5 right-3.5 top-full z-40 mt-1.5 rounded-xl border border-[#24446B] bg-[#1A3252] p-1.5 shadow-[0_12px_32px_rgba(0,0,0,.4)]"
          >
            {#each verticalOptions as v (v.id)}
              <button
                type="button"
                class="mb-0.5 flex min-h-[40px] w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left last:mb-0 hover:bg-white/[.08] {v.id ===
                session.selectedVertical
                  ? 'bg-white/[.08]'
                  : ''}"
                onclick={() => selectVertical(v.id)}
              >
                <span class="h-2 w-2 shrink-0 rounded-full" style="background:{v.dot}"></span>
                <span class="flex-1 text-[13px] font-medium text-white">{v.name}</span>
              </button>
            {/each}
          </div>
        {/if}
      {:else if currentVertical}
        <div class="flex items-center gap-2.5 rounded-[10px] border border-white/10 bg-white/5 px-3 py-[9px]">
          <span class="h-[9px] w-[9px] shrink-0 rounded-full" style="background:{currentVertical.dot}"></span>
          <span class="min-w-0 flex-1">
            <span class="block text-[9px] font-semibold uppercase tracking-[.1em] text-white/40">Vertical</span>
            <span class="block truncate font-heading text-sm font-bold tracking-tight text-white"
              >{currentVertical.name}</span
            >
          </span>
        </div>
      {/if}
    </div>

    <!-- nav -->
    <nav class="flex-1 overflow-y-auto px-3 py-1">
      {#each navItems as item (item.id)}
        {@const active = activeTab === item.id}
        <button
          type="button"
          class="mb-[3px] flex min-h-[44px] w-full items-center gap-3 rounded-lg border-l-[3px] px-3 py-2.5 text-left transition-colors {active
            ? 'border-toyama-lime bg-toyama-lime/10'
            : 'border-transparent hover:bg-white/[.06]'}"
          onclick={() => navigate(item.id)}
          aria-current={active ? 'page' : undefined}
        >
          <item.icon class="h-5 w-5 shrink-0 {active ? 'text-toyama-lime' : 'text-white/70'}" strokeWidth={2} />
          <span class="flex-1 text-sm {active ? 'font-bold text-white' : 'font-medium text-white/80'}">
            {item.label}
          </span>
          {#if (item.id === 'leads' && newLeadsBadge > 0) || (item.id === 'issues' && newIssuesBadge > 0)}
            <span
              class="inline-flex h-5 min-w-[20px] items-center justify-center rounded-pill bg-toyama-lime px-1.5 font-heading text-xs font-bold text-navy"
              title={item.id === 'issues' ? 'New assignments across your accessible teams' : 'Unseen new leads across your accessible teams'}
              aria-label={item.id === 'issues' ? `${newIssuesBadge} new issue assignments` : `${newLeadsBadge} unseen new leads`}
            >
              {item.id === 'issues' ? newIssuesBadge : newLeadsBadge}
            </span>
          {/if}
        </button>
      {/each}
      {#if canSeeEmails}
        {@const active = activeTab === 'emails'}
        <button
          type="button"
          class="mb-[3px] flex min-h-[44px] w-full items-center gap-3 rounded-lg border-l-[3px] px-3 py-2.5 text-left transition-colors {active
            ? 'border-toyama-lime bg-toyama-lime/10'
            : 'border-transparent hover:bg-white/[.06]'}"
          onclick={() => navigate('emails')}
          aria-current={active ? 'page' : undefined}
        >
          <Mail class="h-5 w-5 shrink-0 {active ? 'text-toyama-lime' : 'text-white/70'}" strokeWidth={2} />
          <span class="flex-1 text-sm {active ? 'font-bold text-white' : 'font-medium text-white/80'}">Emails</span>
        </button>
      {/if}
      {#if session.isAdmin && !isIssuesOnly && !isDispatchOnly}
        {@const active = activeTab === 'admin'}
        <button
          type="button"
          class="mb-[3px] flex min-h-[44px] w-full items-center gap-3 rounded-lg border-l-[3px] px-3 py-2.5 text-left transition-colors {active
            ? 'border-toyama-lime bg-toyama-lime/10'
            : 'border-transparent hover:bg-white/[.06]'}"
          onclick={() => navigate('admin')}
          aria-current={active ? 'page' : undefined}
        >
          <ShieldCheck class="h-5 w-5 shrink-0 {active ? 'text-toyama-lime' : 'text-white/70'}" strokeWidth={2} />
          <span class="flex-1 text-sm {active ? 'font-bold text-white' : 'font-medium text-white/80'}">Admin</span>
        </button>
      {/if}
    </nav>

    <!-- user footer -->
    <div class="flex items-center gap-2.5 border-t border-white/[.08] px-3.5 py-3">
      <span
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-full font-heading text-sm font-bold text-navy"
        style="background:linear-gradient(135deg,#226A73,#97D700)"
      >
        {initials}
      </span>
      <span class="min-w-0 flex-1">
        <span class="block truncate text-[13px] font-semibold text-white">
          {session.me?.user.name || session.me?.user.email}
        </span>
        <span class="block truncate text-[11px] font-medium text-toyama-lime">{userSubtitle}</span>
      </span>
      {#if !isIssuesOnly && !isDispatchOnly}
        <button
          type="button"
          onclick={() => navigate('settings')}
          class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white/60 hover:bg-white/10 hover:text-white {activeTab ===
          'settings'
            ? 'bg-white/10 text-white'
            : ''}"
          aria-label="Email settings"
          aria-current={activeTab === 'settings' ? 'page' : undefined}
        >
          <Settings class="h-4 w-4" />
        </button>
      {/if}
      <button
        type="button"
        onclick={onLogout}
        class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white/60 hover:bg-white/10 hover:text-white"
        aria-label="Log out"
      >
        <LogOut class="h-4 w-4" />
      </button>
    </div>
  </aside>

  <!-- ===================== MAIN ===================== -->
  <div class="flex min-w-0 flex-1 flex-col">
    <header
      class="relative z-20 flex h-16 shrink-0 items-center gap-4 border-b border-toyama-cloudy-gray bg-white px-4 md:px-6"
    >
      <button
        type="button"
        class="-ml-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-navy hover:bg-toyama-light-carbon md:hidden"
        aria-label="Open navigation"
        onclick={() => (mobileNavOpen = true)}
      >
        <Menu class="h-5 w-5" />
      </button>
      <div class="min-w-0">
        <h1 class="truncate font-heading text-[19px] font-bold leading-[1.1] tracking-tight text-navy">
          {pageMeta[activeTab]?.title ?? 'Toyama CRM'}
        </h1>
        {#if pageMeta[activeTab]?.subtitle}
          <div class="mt-px truncate text-[11.5px] text-dark-gray">{activeTab === 'my-day' || activeTab === 'dashboard' ? rolePresentation[roleProfile(session.me)].subtitle : pageMeta[activeTab].subtitle}</div>
        {/if}
      </div>
      <span class="flex-1"></span>

      {#if !isIssuesOnly && !isDispatchOnly}
      <!-- desktop search: 320px, hidden on mobile (collapses to icon) -->
      <div class="relative hidden md:block" style="width:320px; max-width:36vw;">
        <Search class="pointer-events-none absolute left-3 top-1/2 h-[17px] w-[17px] -translate-y-1/2 text-dark-gray" />
        <input
          type="search"
          placeholder="Search contacts, companies, deals…"
          bind:value={searchTerm}
          onfocus={() => (searchFocused = true)}
          onblur={onSearchBlur}
          onkeydown={onSearchKeydown}
          class="input h-10 !pl-9 !text-[13.5px]"
        />
        {#if showResults}
          <div
            class="absolute left-0 right-0 top-full z-50 mt-2 rounded-xl border border-toyama-cloudy-gray bg-white p-1.5 shadow-[var(--tym-shadow-3)]"
          >
            {#if searchQuery.isFetching && !searchQuery.data}
              <div class="px-2.5 py-3.5 text-center text-[12.5px] text-dark-gray">Searching&hellip;</div>
            {:else if resultCount === 0}
              <div class="px-2.5 py-3.5 text-center text-[12.5px] text-dark-gray">
                No matches in your accessible verticals.
              </div>
            {:else}
              {#each searchQuery.data?.companies ?? [] as c (c.id)}
                <button
                  type="button"
                  class="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-toyama-light-carbon"
                  onmousedown={() => pickCompany(c)}
                >
                  <Building2 class="h-[17px] w-[17px] shrink-0 text-toyama-teal" strokeWidth={2} />
                  <span class="min-w-0 flex-1">
                    <span class="block truncate text-[13px] font-semibold text-navy">{c.name}</span>
                    <span class="block truncate text-[11px] text-dark-gray">{c.city} &middot; {c.kind}</span>
                  </span>
                  <span class="shrink-0 text-[10px] font-semibold uppercase tracking-[.06em] text-dark-gray">Company</span>
                </button>
              {/each}
              {#each searchQuery.data?.people ?? [] as p (p.id)}
                <button
                  type="button"
                  class="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-toyama-light-carbon"
                  onmousedown={() => pickPerson(p)}
                >
                  <Users class="h-[17px] w-[17px] shrink-0 text-toyama-teal" strokeWidth={2} />
                  <span class="min-w-0 flex-1">
                    <span class="block truncate text-[13px] font-semibold text-navy">{personName(p)}</span>
                    <span class="block truncate text-[11px] text-dark-gray">{p.job_title}</span>
                  </span>
                  <span class="shrink-0 text-[10px] font-semibold uppercase tracking-[.06em] text-dark-gray">Person</span>
                </button>
              {/each}
              {#each searchQuery.data?.deals ?? [] as d (d.id)}
                <button
                  type="button"
                  class="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-toyama-light-carbon"
                  onmousedown={() => pickDeal(d)}
                >
                  <Kanban class="h-[17px] w-[17px] shrink-0 text-toyama-teal" strokeWidth={2} />
                  <span class="min-w-0 flex-1">
                    <span class="block truncate text-[13px] font-semibold text-navy">{d.name}</span>
                    <span class="block truncate text-[11px] text-dark-gray capitalize">{d.stage}</span>
                  </span>
                  <span class="shrink-0 text-[10px] font-semibold uppercase tracking-[.06em] text-dark-gray">Deal</span>
                </button>
              {/each}
            {/if}
          </div>
        {/if}
      </div>

      <!-- mobile search: collapses to an icon that expands full-width -->
      <button
        type="button"
        class="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-navy hover:bg-toyama-light-carbon md:hidden"
        aria-label="Search"
        onclick={() => (mobileSearchOpen = true)}
      >
        <Search class="h-5 w-5" />
      </button>
      {#if mobileSearchOpen}
        <div class="fixed inset-0 z-50 flex flex-col bg-white p-3 md:hidden">
          <div class="flex items-center gap-2">
            <div class="relative flex-1">
              <Search class="pointer-events-none absolute left-3 top-1/2 h-[17px] w-[17px] -translate-y-1/2 text-dark-gray" />
              <!-- svelte-ignore a11y_autofocus -->
              <input
                type="search"
                autofocus
                placeholder="Search contacts, companies, deals…"
                bind:value={searchTerm}
                onfocus={() => (searchFocused = true)}
                onkeydown={onSearchKeydown}
                class="input h-11 !pl-9"
              />
            </div>
            <button
              type="button"
              class="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-navy hover:bg-toyama-light-carbon"
              aria-label="Close search"
              onclick={closeSearch}
            >
              <X class="h-5 w-5" />
            </button>
          </div>
          <div class="mt-3 flex-1 overflow-y-auto">
            {#if debouncedTerm.trim().length === 0}
              <div class="px-2.5 py-3.5 text-center text-[12.5px] text-dark-gray">Start typing to search.</div>
            {:else if resultCount === 0}
              <div class="px-2.5 py-3.5 text-center text-[12.5px] text-dark-gray">
                No matches in your accessible verticals.
              </div>
            {:else}
              {#each searchQuery.data?.companies ?? [] as c (c.id)}
                <button
                  type="button"
                  class="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-left hover:bg-toyama-light-carbon"
                  onclick={() => pickCompany(c)}
                >
                  <Building2 class="h-[17px] w-[17px] shrink-0 text-toyama-teal" strokeWidth={2} />
                  <span class="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-navy">{c.name}</span>
                  <span class="shrink-0 text-[10px] font-semibold uppercase text-dark-gray">Company</span>
                </button>
              {/each}
              {#each searchQuery.data?.people ?? [] as p (p.id)}
                <button
                  type="button"
                  class="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-left hover:bg-toyama-light-carbon"
                  onclick={() => pickPerson(p)}
                >
                  <Users class="h-[17px] w-[17px] shrink-0 text-toyama-teal" strokeWidth={2} />
                  <span class="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-navy">{personName(p)}</span>
                  <span class="shrink-0 text-[10px] font-semibold uppercase text-dark-gray">Person</span>
                </button>
              {/each}
              {#each searchQuery.data?.deals ?? [] as d (d.id)}
                <button
                  type="button"
                  class="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-left hover:bg-toyama-light-carbon"
                  onclick={() => pickDeal(d)}
                >
                  <Kanban class="h-[17px] w-[17px] shrink-0 text-toyama-teal" strokeWidth={2} />
                  <span class="min-w-0 flex-1 truncate text-[13.5px] font-semibold text-navy">{d.name}</span>
                  <span class="shrink-0 text-[10px] font-semibold uppercase text-dark-gray">Deal</span>
                </button>
              {/each}
            {/if}
          </div>
        </div>
      {/if}
      {/if}

      <!-- + New: a plain "New issue" button for an issues_only account (the
           person picker and every other entity are off-limits to it — see
           middleware.RequireNotIssuesOnly), the full dropdown otherwise. -->
      {#if isIssuesOnly}
        <button
          type="button"
          class="flex h-10 items-center gap-[7px] whitespace-nowrap rounded-[10px] bg-toyama-lime px-4 font-heading text-[13.5px] font-bold text-navy hover:bg-[var(--tym-lime-600)]"
          onclick={() => chooseNew('issues')}
          aria-label="New issue"
        >
          <Plus class="h-[17px] w-[17px]" strokeWidth={2.4} />
          <span class="hidden sm:inline">New issue</span>
        </button>
      {:else if !isDispatchOnly}
        <div class="relative">
          <button
            type="button"
            class="flex h-10 items-center gap-[7px] whitespace-nowrap rounded-[10px] bg-toyama-lime px-4 font-heading text-[13.5px] font-bold text-navy hover:bg-[var(--tym-lime-600)]"
            onclick={() => (newMenuOpen = !newMenuOpen)}
            aria-label="Create new record"
            aria-expanded={newMenuOpen}
          >
            <Plus class="h-[17px] w-[17px]" strokeWidth={2.4} />
            <span class="hidden sm:inline">New</span>
          </button>
          {#if newMenuOpen}
            <button
              type="button"
              class="fixed inset-0 z-30 cursor-default"
              aria-label="Close new-item menu"
              onclick={() => (newMenuOpen = false)}
            ></button>
            <div
              class="absolute right-0 top-full z-40 mt-1.5 w-48 rounded-xl border border-toyama-cloudy-gray bg-white p-1.5 shadow-[var(--tym-shadow-3)]"
            >
              {#each newMenuItems as item (item.tab)}
                <button
                  type="button"
                  class="block w-full rounded-lg px-3 py-2 text-left text-[13px] font-medium text-navy hover:bg-toyama-light-carbon"
                  onclick={() => chooseNew(item.tab)}
                >
                  {item.label}
                </button>
              {/each}
            </div>
          {/if}
        </div>
      {/if}
    </header>

    <main class="flex-1 overflow-y-auto overflow-x-hidden bg-toyama-light-carbon {!isIssuesOnly && !isDispatchOnly ? 'crm-main-with-mobile-nav' : ''}">
      {@render children()}
    </main>
    {#if !isIssuesOnly && !isDispatchOnly}
      <button type="button" class="crm-mobile-log" onclick={() => onLog ? onLog() : chooseNew('activities')} aria-label="Log or plan a touchpoint"><Plus size={20} /> Log</button>
      <nav class="crm-mobile-bottom-nav" aria-label="Daily work navigation">
        <button type="button" aria-current={activeTab === 'my-day' ? 'page' : undefined} onclick={() => navigate('my-day')}><LayoutGrid size={20} /><span>My day</span></button>
        <button type="button" aria-current={activeTab === 'people' ? 'page' : undefined} onclick={() => navigate('people')}><Users size={20} /><span>Contacts</span></button>
        <button type="button" aria-current={activeTab === 'activities' ? 'page' : undefined} onclick={() => navigate('activities')}><Activity size={20} /><span>Agenda</span></button>
        <button type="button" onclick={() => { mobileSearchOpen = true; searchFocused = true; }}><Search size={20} /><span>Search</span></button>
      </nav>
    {/if}
  </div>
</div>
